/**
 * Slot Definition Helpers
 *
 * Helpers for building SlotRegistry objects.
 * Slots are organized by LOCATION, with each location containing an array
 * of control definitions.
 *
 * Usage:
 *   const slots: SlotRegistry = {
 *     'content-header': [stepSlider(), runDropdown()],
 *     'content-footer': [indexSlider()],
 *   };
 */

import {ControlDefinition, SlotRegistry} from '../types';

// ============================================================================
// Control Definition Helpers
// ============================================================================

/**
 * Creates a step slider control definition.
 *
 * @param linked - Whether control is linked across all tiles (default: true)
 * @returns Control definition for step slider
 */
export function stepSlider(linked: boolean = true): ControlDefinition {
  return {
    control: 'step',
    type: 'slider',
    linked,
  };
}

/**
 * Creates a step tabs control definition.
 *
 * @param linked - Whether control is linked across all tiles (default: false)
 * @returns Control definition for step tabs
 */
export function stepTabs(linked: boolean = false): ControlDefinition {
  return {
    control: 'step',
    type: 'tabs',
    linked,
  };
}

/**
 * Creates an index slider control definition.
 *
 * @param linked - Whether control is linked across all tiles (default: true)
 * @returns Control definition for index slider
 */
export function indexSlider(linked: boolean = true): ControlDefinition {
  return {
    control: 'index',
    type: 'slider',
    linked,
  };
}

/**
 * Creates an index tabs control definition.
 *
 * @param linked - Whether control is linked across all tiles (default: false)
 * @returns Control definition for index tabs
 */
export function indexTabs(linked: boolean = false): ControlDefinition {
  return {
    control: 'index',
    type: 'tabs',
    linked,
  };
}

/**
 * Creates a run dropdown control definition.
 *
 * @param linked - Whether control is linked across all tiles (default: true)
 * @returns Control definition for run dropdown
 */
export function runDropdown(linked: boolean = true): ControlDefinition {
  return {
    control: 'run',
    type: 'dropdown',
    linked,
  };
}

/**
 * Creates an x-axis range slider control definition (SelectableAxis).
 *
 * Range sliders always affect all tiles (linked=true).
 *
 * @returns Control definition for x-axis range slider
 */
export function xAxisRangeSlider(): ControlDefinition {
  return {
    control: 'xAxisRange',
    type: 'range-slider',
    linked: true,
  };
}

/**
 * Creates a y-axis range slider control definition (SelectableAxis).
 *
 * Range sliders always affect all tiles (linked=true).
 *
 * @returns Control definition for y-axis range slider
 */
export function yAxisRangeSlider(): ControlDefinition {
  return {
    control: 'yAxisRange',
    type: 'range-slider',
    linked: true,
  };
}

/**
 * Creates a label control definition.
 *
 * Label controls are not rendered as interactive controls, but are shown as static
 * text labels in headers/footers to display dimension values (run name, step, index).
 *
 * @param control - Which dimension to show as a label
 * @returns Control definition for label control
 */
export function label(control: 'step' | 'index' | 'run'): ControlDefinition {
  return {
    control,
    type: 'label',
    linked: false,
  };
}

/**
 * Creates a caption control definition.
 *
 * Caption controls render media captions in the tile-footer slot.
 *
 * @returns Control definition for caption control
 */
export function caption(): ControlDefinition {
  return {
    control: 'caption',
    type: 'label',
    linked: false,
  };
}

// ============================================================================
// Default Slot Registries
// ============================================================================

/**
 * Default slot registry for traditional panel mode.
 *
 * All controls visible as linked sliders/dropdowns in content header/footer.
 */
export const DEFAULT_SLOTS: SlotRegistry = {
  'content-header': [stepSlider(), runDropdown()],
  'content-footer': [indexSlider()],
};
